import { ErrorResponse } from 'oidc-client-ts'
import type { ErrorContext } from 'react-oidc-context'

/**
 * The query parameters the identity provider adds when it sends
 * the user back to us after a sign in, successful or not.
 */
const AUTH_RESPONSE_PARAMS = [
  'code',
  'state',
  'session_state',
  'iss',
  'error',
  'error_description',
  'error_uri',
]

const EXPIRED_RETRY_KEY =
  'soniq:auth:authentication-expired-retry'

/**
 * Keycloak sends `authentication_expired` when the user still has
 * a Keycloak session but the sign in page in this tab has expired
 * (left open too long, or reached with back/forward).
 *
 * Keycloak's advice is to start a new sign in straight away. The
 * live session means the user comes back signed in without having
 * to type anything.
 */
export function isAuthenticationExpired(
  error: ErrorContext
): boolean {
  const inner = error.innerError
  return (
    inner instanceof ErrorResponse &&
    inner.error === 'temporarily_unavailable' &&
    inner.error_description === 'authentication_expired'
  )
}

/**
 * What `fetch` throws when the request never reached the server,
 * in Firefox, Chrome and Safari.
 */
const FETCH_NETWORK_ERROR =
  /^(NetworkError when attempting to fetch resource\.|Failed to fetch|Load failed)$/

/**
 * Renewing the access token in the background failed because the
 * browser could not reach Keycloak: it was offline, or the device
 * had just woken from sleep.
 */
export function isRenewOffline(error: ErrorContext): boolean {
  const inner = error.innerError
  return (
    error.source === 'renewSilent' &&
    inner instanceof TypeError &&
    FETCH_NETWORK_ERROR.test(inner.message)
  )
}

/**
 * The sign in round trip went stale rather than broke: the session
 * expired at Keycloak, or the sign in response was already used
 * (e.g. the page was reloaded). A fresh sign in fixes both, so
 * they are not worth reporting.
 *
 * Renewing the token while offline is not a fault in the app
 * either, so it is not reported.
 */
export function isExpectedSigninError(
  error: ErrorContext
): boolean {
  return (
    isAuthenticationExpired(error) ||
    error.message === 'No matching state found in storage' ||
    isRenewOffline(error)
  )
}

/**
 * Remove the sign in response from the address bar, keeping any
 * other query parameters.
 *
 * A sign in response can only be used once, so leaving it there
 * makes every reload fail again.
 */
export function clearAuthParams(): void {
  const url = new URL(window.location.href)
  for (const param of AUTH_RESPONSE_PARAMS) {
    url.searchParams.delete(param)
  }
  window.history.replaceState(
    window.history.state,
    document.title,
    url.toString()
  )
}

/**
 * Only retry an expired sign in once per tab until the user signs
 * in, so a Keycloak that keeps answering `authentication_expired`
 * can't send the user round in a loop.
 */
export function hasRetriedExpiredSignin(): boolean {
  return sessionStorage.getItem(EXPIRED_RETRY_KEY) !== null
}

export function markExpiredSigninRetried(): void {
  sessionStorage.setItem(EXPIRED_RETRY_KEY, '1')
}

export function resetExpiredSigninRetry(): void {
  sessionStorage.removeItem(EXPIRED_RETRY_KEY)
}
